/**
 * Contact detail screen with create, read, and update modes for the Address Book modal.
 */

import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonIcon,
  IonAlert,
  IonActionSheet,
  useIonViewWillLeave,
} from '@ionic/react';
import { trashOutline, addCircle, createOutline } from 'ionicons/icons';
import { useIonRouter } from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { useContactState } from './ContactStateContext';
import { selectAllListingCategories } from './reducer';
import { Contact } from './types';
import { ContactLogic } from '@/modules/contact';
import { PhoneLogic } from '@/modules/phone';
import type { PhoneType } from '@/modules/phone';
import { useContactEditForm } from './useContactEditForm';
import ContactEditFormBody from './ContactEditFormBody';
import SectionCard from '@/components/SectionCard';
import OverflowMenu from '@/components/OverflowMenu';
import { pxToRem } from '@/modules/config/constants';

// Where IonBackButton lands when this page was deep-linked and there is no
// previous page on the Ionic stack to pop back to.
const CONTACTS_LIST_HREF = '/ab/lists/all/contacts';

export type ContactViewMode = 'create' | 'view' | 'update';

interface ContactViewProps {
  initialMode: ContactViewMode;
  contactId?: string;
}

function getPhoneTypeLabel(type: PhoneType | undefined): string {
  return PhoneLogic.getPhoneTypeLabel(type);
}

const emptyContact: Contact = {
  localId: '',
  prefix: '',
  firstName: '',
  middleName: '',
  lastName: '',
  suffix: '',
  nickname: '',
  tels: [],
  listingCategories: [],
};

const pageHeaderRowStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  minHeight: '44px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const pageHeaderTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: pxToRem(24),
  fontWeight: 600,
  textAlign: 'center',
};
const pageShellStyle: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const ContactView: React.FC<ContactViewProps> = ({
  initialMode,
  contactId,
}) => {
  const router = useIonRouter();
  const { state, dispatch } = useContactState();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const [isEditing, setIsEditing] = useState(initialMode !== 'view');
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  const [showAddToListSheet, setShowAddToListSheet] = useState(false);
  const [showNewListAlert, setShowNewListAlert] = useState(false);

  const isCreateMode = initialMode === 'create';
  const contact: Contact | undefined = contactId
    ? state.byId[contactId]
    : undefined;
  const formContact = contact ?? emptyContact;
  const allListingCategories = selectAllListingCategories(state);

  const form = useContactEditForm(formContact, isEditing);

  const handleDeleteConfirm = () => {
    if (contact) {
      dispatch({
        type: 'REMOVE_CONTACT',
        payload: { localId: contact.localId },
      });
      setShowDeleteSheet(false);
      presentToast({
        message: 'Contact deleted successfully',
        duration: 2000,
        color: 'success',
      });
      router.goBack();
    }
  };

  const handleAddToList = (category: string) => {
    if (contact && !contact.listingCategories.includes(category)) {
      dispatch({
        type: 'UPDATE_CONTACT',
        payload: {
          localId: contact.localId,
          fields: {
            listingCategories: [...contact.listingCategories, category],
          },
        },
      });
    }
  };

  const handleCreateNewList = (name: string) => {
    const trimmed = name.trim();
    if (contact && trimmed && !contact.listingCategories.includes(trimmed)) {
      dispatch({
        type: 'UPDATE_CONTACT',
        payload: {
          localId: contact.localId,
          fields: {
            listingCategories: [...contact.listingCategories, trimmed],
          },
        },
      });
    }
  };

  const handleDone = () => {
    form.submit(fields => {
      if (isCreateMode) {
        const result = ContactLogic.createReachable(fields);
        if (!result) {
          presentToast({
            message: 'At least one valid U.S. phone number is required',
            duration: 2000,
            color: 'danger',
          });
          return;
        }
        dispatch({ type: 'ADD_CONTACTS', payload: [result.contact] });
        presentToast({
          message: 'Contact added successfully',
          duration: 2000,
          color: 'success',
        });
        router.push(
          '/ab/contacts/' + result.contact.localId,
          'forward',
          'replace'
        );
        return;
      }

      if (!contactId) return;

      dispatch({
        type: 'UPDATE_CONTACT',
        payload: {
          localId: contactId,
          fields,
        },
      });
      presentToast({
        message: 'Contact updated successfully',
        duration: 2000,
        color: 'success',
      });
      if (initialMode === 'update') {
        router.push('/ab/contacts/' + contactId, 'back', 'replace');
      } else {
        setIsEditing(false);
      }
    });
  };

  const handleCancel = () => {
    if (initialMode === 'create' || initialMode === 'update') {
      router.goBack();
      return;
    }
    setIsEditing(false);
  };

  if (isCreateMode) {
    return (
      <IonPage data-testid="contact-view">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                onClick={handleCancel}
                aria-label="Cancel"
              >
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle className="ion-hide-accessible">New Contact</IonTitle>
            <IonButtons slot="end">
              <IonButton
                strong={true}
                onClick={handleDone}
                aria-label="Done"
                disabled={!form.hasValidPhone}
              >
                Add
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent
          fullscreen
          style={{ '--background': 'var(--app-page-background)' }}
        >
          <ContactEditFormBody
            {...form}
            isAddMode
            allListingCategories={allListingCategories}
          />
        </IonContent>
      </IonPage>
    );
  }

  // Contact not found
  if (!contact) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref={CONTACTS_LIST_HREF} />
            </IonButtons>
            <IonTitle>Contact Not Found</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Contact not found.</p>
            <IonButton onClick={() => router.goBack()}>
              Back to Contacts
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const displayName = ContactLogic.displayName(contact);
  const companyName = contact.companyName?.trim();
  // Only show the company as a subtitle when it isn't already the display name
  // (a nameless business contact already shows its company as the title).
  const showCompanySubtitle = !!companyName && companyName !== displayName;
  const initials = ContactLogic.getInitials(contact);
  const hasListingCategories = contact.listingCategories.length > 0;

  const addToListButtons = [
    ...allListingCategories.map(cat => {
      const alreadyAssigned = contact.listingCategories.includes(cat);
      return {
        text: cat,
        cssClass: alreadyAssigned ? 'action-sheet-disabled' : '',
        handler: () => {
          if (!alreadyAssigned) handleAddToList(cat);
          return !alreadyAssigned;
        },
      };
    }),
    {
      text: 'New List...',
      handler: () => {
        setShowAddToListSheet(false);
        setTimeout(() => setShowNewListAlert(true), 300);
        return true;
      },
    },
    { text: 'Cancel', role: 'cancel' as const },
  ];

  return (
    <IonPage>
      <div data-testid="address-book-contact-card" style={pageShellStyle}>
        <IonHeader>
          <IonToolbar>
            {isEditing ? (
              <>
                <IonButtons slot="start">
                  <IonButton
                    onClick={handleCancel}
                    aria-label="Cancel"
                  >
                    Cancel
                  </IonButton>
                </IonButtons>
                <IonTitle className="ion-hide-accessible">
                  Edit Contact
                </IonTitle>
                <IonButtons slot="end">
                  <IonButton
                    strong={true}
                    onClick={handleDone}
                    aria-label="Done"
                  >
                    Done
                  </IonButton>
                </IonButtons>
              </>
            ) : (
              <>
                <IonButtons slot="start">
                  <IonBackButton defaultHref={CONTACTS_LIST_HREF} />
                </IonButtons>
                <IonTitle className="ion-hide-accessible">
                  Contact Details
                </IonTitle>
              </>
            )}
          </IonToolbar>
        </IonHeader>

        <IonContent style={{ '--background': 'var(--app-page-background)' }}>
          {isEditing ? (
            <ContactEditFormBody
              {...form}
              allListingCategories={allListingCategories}
            />
          ) : (
            <>
              {/* Contact Header */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '24px 20px 8px',
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#c4c4c6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: pxToRem(32),
                      fontWeight: 500,
                      color: '#fff',
                    }}
                  >
                    {initials || '?'}
                  </span>
                </div>
                <div style={pageHeaderRowStyle}>
                  <h1 style={pageHeaderTitleStyle}>{displayName}</h1>
                  <OverflowMenu
                    ariaLabel="Contact Actions"
                    buttonTestId="contact-actions-menu-button"
                  >
                    {({ close, menuItemStyle }) => (
                      <>
                        <IonButton
                          expand="block"
                          fill="clear"
                          onClick={() => {
                            close();
                            setIsEditing(true);
                          }}
                          aria-label="Edit Contact"
                          data-testid="edit-contact-menu-item"
                          style={menuItemStyle}
                        >
                          <IonIcon icon={createOutline} slot="start" />
                          Edit Contact
                        </IonButton>
                        <IonButton
                          expand="block"
                          fill="clear"
                          color="danger"
                          onClick={() => {
                            close();
                            setShowDeleteSheet(true);
                          }}
                          aria-label="Delete Contact"
                          data-testid="delete-contact-menu-item"
                          style={menuItemStyle}
                        >
                          <IonIcon icon={trashOutline} slot="start" />
                          Delete Contact
                        </IonButton>
                      </>
                    )}
                  </OverflowMenu>
                </div>
                {showCompanySubtitle && (
                  <p
                    data-testid="contact-company"
                    style={{
                      margin: '2px 0 0',
                      fontSize: pxToRem(16),
                      color: 'var(--ion-color-medium)',
                      textAlign: 'center',
                    }}
                  >
                    {companyName}
                  </p>
                )}
              </div>

              <div style={{ padding: '8px 16px 20px' }}>
                {/* Phone Numbers */}
                {contact.tels.length > 0 && (
                  <SectionCard>
                    {contact.tels.map((tel, index) => (
                      <IonItem
                        key={tel.localId || index}
                        lines={
                          index < contact.tels.length - 1 ? 'inset' : 'none'
                        }
                      >
                        <IonLabel>
                          <p
                            className="section-card__phone-type-label"
                            style={{
                              fontSize: pxToRem(13),
                              color: '#000',
                              marginBottom: '2px',
                            }}
                          >
                            {getPhoneTypeLabel(tel.type)}
                          </p>
                          <h2
                            style={{
                              fontSize: pxToRem(16),
                              color: 'var(--ion-color-secondary)',
                            }}
                          >
                            {ContactLogic.getFormattedPhone(
                              contact,
                              tel.localId
                            ) || tel.value}
                          </h2>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </SectionCard>
                )}

                {/* Included in Lists */}
                <div data-testid="included-in-lists-section">
                  <SectionCard title="Included in Lists">
                    {contact.listingCategories.map((category, index) => (
                      <IonItem
                        key={index}
                        lines="inset"
                        data-testid={`list-membership-item-${index}`}
                      >
                        <IonLabel>{category}</IonLabel>
                      </IonItem>
                    ))}
                    <IonItem
                      button
                      lines="none"
                      onClick={() => setShowAddToListSheet(true)}
                      data-testid={
                        hasListingCategories
                          ? 'add-to-list-row'
                          : 'add-to-list-button'
                      }
                    >
                      <IonButton
                        slot="start"
                        fill="clear"
                        color="secondary"
                        size="small"
                        style={{ marginInlineEnd: '4px' }}
                        className="contact-view__add-to-list-icon-btn"
                      >
                        <IonIcon slot="icon-only" icon={addCircle} />
                      </IonButton>
                      <IonLabel
                        color="secondary"
                        className="contact-view__add-to-list-label"
                      >
                        Add to list
                      </IonLabel>
                    </IonItem>
                  </SectionCard>
                </div>
              </div>
            </>
          )}

          {/* Delete Confirmation Action Sheet */}
          <IonActionSheet
            isOpen={showDeleteSheet}
            onDidDismiss={() => setShowDeleteSheet(false)}
            header={`Remove ${displayName} from your Address Book?`}
            buttons={[
              {
                text: 'Delete Contact',
                role: 'destructive',
                handler: handleDeleteConfirm,
              },
              {
                text: 'Cancel',
                role: 'cancel',
              },
            ]}
          />

          {/* Add to List action sheet */}
          <IonActionSheet
            isOpen={showAddToListSheet}
            onDidDismiss={() => setShowAddToListSheet(false)}
            header="Add to List"
            buttons={addToListButtons}
          />

          {/* New List alert */}
          {showNewListAlert && (
            <IonAlert
              isOpen={showNewListAlert}
              onDidDismiss={() => setShowNewListAlert(false)}
              header="New List"
              inputs={[
                {
                  name: 'listName',
                  type: 'text',
                  placeholder: 'List name',
                },
              ]}
              buttons={[
                { text: 'Cancel', role: 'cancel' },
                {
                  text: 'OK',
                  handler: (data: { listName: string }) => {
                    handleCreateNewList(data.listName || '');
                  },
                },
              ]}
            />
          )}
        </IonContent>
      </div>
    </IonPage>
  );
};

export default ContactView;
